import React, {
  memo,
  useCallback,
  useMemo,
  forwardRef,
  CSSProperties,
} from 'react'
import ReactSelect, {
  components as ReactSelectComponents,
  MenuListProps,
  SingleValueProps,
  OptionProps,
  ContainerProps,
  SelectComponentsConfig,
  MenuPlacement,
  DropdownIndicatorProps,
  MenuProps,
  FilterOptionOption,
} from 'react-select'

import styled from '@emotion/styled'
import { isPlainObject } from 'is-plain-object'
import {
  map,
  pipe,
  when,
  complement,
  allPass,
  filter,
  length,
  equals,
} from 'ramda'
import CreatableSelect from 'react-select/creatable'

import { objectToArray } from '@surglogs/util'
import { isFunction } from '@surglogs/util.is-function'

import { useHandler, useMemoizedCall } from '@surglogs-app/hooks'

import { Loader } from '../../../components'
import { Box } from '../Box'
import { DropdownIcon } from '../DropdownIcon'
import { FlexBox } from '../FlexBox'
import { Text, TextBold } from '../Text'

import { CreatedSelectOption } from './SelectOption'
import { SelectOptionWithSubOptions } from './SelectOptionWithSubOptions'
import { ISelectOption } from './types'
import { useCustomStyles } from './useCustomStyles'

const hasPlainObject = pipe(filter(isPlainObject), length as any, Boolean)
const hasNotPlainObject = complement(hasPlainObject)

const mapToObjects = when<any, { text: string; id: string }[]>(
  allPass([Array.isArray, hasNotPlainObject]),
  map((text: string) => ({ id: text, text })),
)

const TEMP_LABEL_KEY = '$KEY'

const SelectWrapper = styled(FlexBox)`
  flex: 1;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  font-family: ${({ theme }) => theme.ui.typography.fontFamilies.default};
  height: ${({ theme }) => theme.ui.spacings.xxl};
`

const Menu = (props: MenuProps) => {
  const { isLoading, options } = props.selectProps

  return (
    <ReactSelectComponents.Menu {...{ ...props, ...props.innerProps }}>
      {props.children}
      {isLoading && options.length ? (
        <Box
          {...{
            bgc: 'transparent.90',
            w: '100%',
            h: '100%',
            position: 'absolute',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            bra: 'sm',
          }}>
          <Loader />
        </Box>
      ) : null}
    </ReactSelectComponents.Menu>
  )
}

const MenuList = (props: MenuListProps) => {
  const innerProps = useMemo(
    // eslint-disable-next-line react/destructuring-assignment
    () => ({ ...props.innerProps, role: 'group' }),
    [props],
  )
  return <ReactSelectComponents.MenuList {...props} {...{ innerProps }} />
}

const SingleValue = ({ children, selectProps, ...props }: SingleValueProps) => {
  const { valuePrefix } = selectProps as SelectLegacyProps
  const innerProps = useMemo(
    () => ({
      // eslint-disable-next-line react/destructuring-assignment
      ...props.innerProps,
      role: 'option',
      'aria-label': 'selected-option',
    }),
    [props],
  )

  return (
    <ReactSelectComponents.SingleValue
      {...props}
      {...{ selectProps, innerProps }}>
      {valuePrefix != null ? (
        <Text {...{ color: 'inherit' }}>
          {valuePrefix}{' '}
          <TextBold {...{ color: 'inherit' }}>{children}</TextBold>
        </Text>
      ) : (
        children
      )}
    </ReactSelectComponents.SingleValue>
  )
}

const Option = memo(
  ({ valueKey, ...props }: OptionProps & { valueKey: string }) => {
    const data = props.data as ISelectOption
    const innerProps = {
      ...props.innerProps,
      role: 'option',
      'aria-label': data[valueKey],
    }
    return <ReactSelectComponents.Option {...props} {...{ innerProps }} />
  },
)

const SelectContainer = memo((props: ContainerProps) => {
  const innerProps = useMemo(
    () => ({
      ...props.innerProps,
      role: 'listbox',
      'aria-label': props.selectProps?.['aria-label'],
      'aria-disabled': props.selectProps?.isDisabled,
    }),
    [props],
  )

  return (
    <ReactSelectComponents.SelectContainer {...props} {...{ innerProps }} />
  )
})

const DropdownIndicator = memo((props: DropdownIndicatorProps) => {
  return (
    <DropdownIcon
      {...{ width: 12, height: 24, isExpanded: props.selectProps.menuIsOpen }}
    />
  )
})

// TODO: add proper type for options
const findValue = (options: any, value: any, valueKey: string) =>
  options
    .map((option: any) => {
      if (Array.isArray(option?.options)) {
        return findValue(option?.options, value, valueKey)
      }

      if (equals(option[valueKey], value)) {
        return option
      }

      return false
    })
    .filter(Boolean)
    .pop()

const getDisplayValue = (options: any[], value: any, valueKey: string) => {
  const selectedOption = findValue(options, value, valueKey)
  return selectedOption ?? null // null, so select value can be cleared/reset
}

type SelectDataProps = Record<string, any> & {
  disabled?: boolean
  subOptionsDefaultValue?: string
  subOptions?: any[]
  subOptionsLabel?: string
  onSubOptionClick?: (options: string[]) => void
}

/** @deprecated  */
export interface SelectLegacyProps {
  valueKey?: string
  labelKey?: string | ((entity: any) => string)
  data?: SelectDataProps[]
  creatable?: boolean
  components?: SelectComponentsConfig<any, any, any>
  invalid?: boolean
  warning?: boolean
  menuPlacement?: MenuPlacement
  onSelect?: (id: any, selected: any) => void
  onChange?: (id: any, selected: any) => void
  value?: number | string
  disabled?: boolean
  'aria-label'?: string
  id?: number | string
  className?: string
  style?: CSSProperties
  menuInPortal?: boolean
  valuePrefix?: string
  filterOption?:
    | ((
        option: FilterOptionOption<SelectDataProps>,
        inputValue: string,
      ) => boolean)
    | null
  menuIsOpen?: boolean
  inputValue?: string
  createOptionLabel?: string
}

/** @deprecated  - use Select instead */
export const SelectLegacy = memo(
  forwardRef<HTMLInputElement, SelectLegacyProps>(
    (
      {
        valueKey = 'id',
        labelKey: labelKeyProp = 'text',
        data,
        onSelect,
        onChange: changeHandler,
        value,
        creatable = false,
        components: parentComponents,
        disabled,
        invalid = false,
        warning = false,
        'aria-label': label,
        menuPlacement = 'auto',
        id: inputId,
        className,
        style,
        menuInPortal = false,
        valuePrefix,
        createOptionLabel = 'Create new option',
        ...rest
      },
      ref,
    ) => {
      const labelKey = useMemo(
        () => (isFunction(labelKeyProp) ? TEMP_LABEL_KEY : labelKeyProp),
        [labelKeyProp],
      )
      const hasSubOptions = useMemoizedCall(
        (_data) => {
          if (!Array.isArray(_data)) {
            return false
          }

          return _data?.find((item: any) => item.subOptions?.length > 0)
        },
        [data],
      )

      const SelectOption = useCallback(
        (args: OptionProps) => <Option {...{ ...args, valueKey }} />,
        [valueKey],
      )

      const components = useMemo(
        () => ({
          MenuList,
          SelectContainer,
          SingleValue,
          Option: hasSubOptions ? SelectOptionWithSubOptions : SelectOption,
          DropdownIndicator: DropdownIndicator,
          Menu,
          ...parentComponents,
        }),
        [hasSubOptions, SelectOption, parentComponents],
      )
      const styles = useCustomStyles({ hasSubOptions, menuInPortal })

      const options = useMemo(
        () =>
          // @ts-ignore
          pipe(objectToArray, mapToObjects, (opts: any) => {
            if (isFunction(labelKeyProp)) {
              return when(
                Array.isArray,
                map((item: any) => ({
                  ...item,
                  [TEMP_LABEL_KEY]: labelKeyProp(item),
                })),
              )(opts)
            }
            return opts
            // @ts-ignore
          })(data ?? []),
        [data, labelKeyProp],
      )

      const onChange = useCallback(
        (selected: any) => {
          if (selected === null) {
            return
          }

          // eslint-disable-next-line no-underscore-dangle
          if (!selected.__isNew__ && value === selected[valueKey]) {
            return
          }

          const { [valueKey]: id, [labelKey]: text } = selected
          if (isFunction(onSelect)) {
            onSelect(id, selected)
          } else if (isFunction(changeHandler)) {
            changeHandler(id, selected)
          }

          return { [valueKey]: id, [labelKey]: text }
        },
        [changeHandler, onSelect, labelKey, valueKey, value],
      )

      const displayValue = useMemoizedCall(getDisplayValue, [
        options,
        value,
        valueKey,
      ])

      const getOptionLabel = useHandler((option: ISelectOption) => {
        // eslint-disable-next-line no-underscore-dangle
        if (option.__isNew__) {
          return option.label
        }
        return option[labelKey]
      })
      const getOptionValue = useHandler((option: ISelectOption) => {
        // eslint-disable-next-line no-underscore-dangle
        if (option.__isNew__) {
          return option.value
        }
        return option[valueKey]
      })

      const isOptionDisabled = useHandler(
        (option: ISelectOption) => option.disabled,
      )

      const selectProps = {
        ...rest,
        getOptionLabel,
        isOptionDisabled,
        getOptionValue,
        styles,
        options,
        value: displayValue,
        valuePrefix,
        isDisabled: disabled,
        className: disabled ? 'is-disabled' : '',
        onChange,
        'aria-label': label ?? (displayValue ? displayValue[valueKey] : ''),
        'aria-invalid': invalid,
        warning,
        invalid,
        components,
        inputId,
        menuPlacement,
        ref,
        ...(menuInPortal
          ? { menuPortalTarget: document.body, menuPosition: 'absolute' }
          : {}),
      } as any

      const formatCreateLabel = useCallback(
        (val: string) => {
          return <CreatedSelectOption {...{ value: val, createOptionLabel }} />
        },
        [createOptionLabel],
      )

      return (
        <SelectWrapper {...{ className, style }}>
          {creatable ? (
            <CreatableSelect {...{ formatCreateLabel, ...selectProps }} />
          ) : (
            <ReactSelect {...selectProps} />
          )}
        </SelectWrapper>
      )
    },
  ),
)
